import React from "react";
import { BlogPost } from "@/types/blog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Highlight, themes } from "prism-react-renderer";
import { cn } from "@/lib/utils";
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import { Components, CodeProps } from "react-markdown";

interface BlogContentProps {
  post?: BlogPost;
  content?: string;
}

const BlogContent: React.FC<BlogContentProps> = ({ post, content }) => {
  const markdownContent = content || post?.content;
  
  if (!markdownContent) {
    return null;
  }

  const components: Components = {
    h1: ({ node, ...props }) => (
      <h1 className="text-4xl font-bold text-sauna-navy mt-8 mb-4" {...props} />
    ),
    h2: ({ node, ...props }) => (
      <h2 className="text-3xl font-bold text-sauna-navy mt-8 mb-4 scroll-mt-20" {...props} />
    ),
    h3: ({ node, ...props }) => (
      <h3 className="text-2xl font-bold text-sauna-navy mt-6 mb-3 scroll-mt-20" {...props} />
    ),
    p: ({ node, ...props }) => (
      <p className="text-gray-700 leading-relaxed mb-4" {...props} />
    ),
    a: ({ node, href, ...props }) => (
      <a
        href={href}
        className="text-sauna-blue hover:text-sauna-orange underline transition-colors"
        target={href?.startsWith("http") ? "_blank" : undefined}
        rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
        {...props}
      />
    ),
    ul: ({ node, ...props }) => (
      <ul className="list-disc list-inside mb-4 space-y-2" {...props} />
    ),
    ol: ({ node, ...props }) => (
      <ol className="list-decimal list-inside mb-4 space-y-2" {...props} />
    ),
    li: ({ node, ...props }) => (
      <li className="text-gray-700" {...props} />
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote
        className="border-l-4 border-sauna-orange pl-4 italic text-gray-600 my-6"
        {...props}
      />
    ),
    code: ({ inline, className, children, ...props }: CodeProps) => {
      const match = /language-(\w+)/.exec(className || "");
      return !inline && match ? (
        <div className="relative group">
          <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => {
                navigator.clipboard.writeText(String(children));
              }}
              className="text-xs bg-sauna-navy/10 hover:bg-sauna-navy/20 text-sauna-navy px-2 py-1 rounded"
            >
              Copy
            </button>
          </div>
          <Highlight
            theme={themes.vsDark}
            code={String(children).replace(/\n$/, "")}
            language={match[1]}
          >
            {({ className, style, tokens, getLineProps, getTokenProps }) => (
              <pre 
                className={cn(
                  "rounded-lg !bg-sauna-navy/5 !mt-0 !mb-4 overflow-x-auto",
                  className
                )}
                style={style}
              >
                {tokens.map((line, i) => (
                  <div key={i} {...getLineProps({ line })}>
                    {line.map((token, key) => (
                      <span key={key} {...getTokenProps({ token })} />
                    ))}
                  </div>
                ))}
              </pre>
            )}
          </Highlight>
        </div>
      ) : (
        <code
          className={cn(
            "bg-sauna-navy/5 text-sauna-navy px-1.5 py-0.5 rounded text-sm",
            className
          )}
          {...props}
        >
          {children}
        </code>
      );
    },
    img: ({ node, src, alt, ...props }) => (
      <div className="my-8">
        <img
          src={src}
          alt={alt}
          className="rounded-lg shadow-lg w-full"
          {...props}
        />
        {alt && (
          <p className="text-center text-sm text-gray-500 mt-2">{alt}</p>
        )}
      </div>
    ),
    table: ({ node, ...props }) => (
      <div className="overflow-x-auto my-6">
        <table className="min-w-full divide-y divide-gray-200" {...props} />
      </div>
    ),
    th: ({ node, ...props }) => (
      <th
        className="px-6 py-3 bg-sauna-navy/5 text-left text-xs font-medium text-sauna-navy uppercase tracking-wider"
        {...props}
      />
    ),
    td: ({ node, ...props }) => (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" {...props} />
    ),
    hr: ({ node, ...props }) => (
      <hr className="my-8 border-t border-gray-200" {...props} />
    ),
  };

  return (
    <div className="prose prose-sauna max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug, rehypeAutolinkHeadings]}
        components={components}
      >
        {markdownContent}
      </ReactMarkdown>
    </div>
  );
};

export default BlogContent;
